import type { Contagens, Filtro, LinhaCrianca } from './types';

// O endereço que a direção manda no WhatsApp é sempre o do site público.
const SITE = 'https://www.seligacatolico.com.br';

export function normalizar(valor?: string | number | null): string {
  if (valor === null || valor === undefined) return '';
  return String(valor).trim();
}

function comparar(valor?: string | number | null): string {
  return normalizar(valor)
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase();
}

export function ouTraco(valor?: string | number | null): string {
  const texto = normalizar(valor);
  if (!texto || texto === '-' || texto === '—') return '—';
  return texto;
}

export function temAlergia(linha: LinhaCrianca): boolean {
  const t = comparar(linha.alergias);
  if (!t || t === '-' || t === '—' || t === 'nao' || t === 'nada') return false;
  if (t.startsWith('nenhum') || t.startsWith('nao tem') || t.startsWith('sem alergia')) return false;
  return true;
}

export function tomaRemedio(linha: LinhaCrianca): boolean {
  const t = comparar(linha.remedios);
  if (!t || t === '-' || t === '—' || t === 'nao') return false;
  if (t.startsWith('nenhum') || t.startsWith('nao toma')) return false;
  return true;
}

export function temRestricao(linha: LinhaCrianca): boolean {
  const t = comparar(linha.alimentacao);
  if (!t || t === '-' || t === '—') return false;
  if (t.startsWith('come de tudo') || t.startsWith('sem restri')) return false;
  return true;
}

export function termoAssinado(linha: LinhaCrianca): boolean {
  return comparar(linha.situacao) === 'completo';
}

export function temCrianca(linha: LinhaCrianca): boolean {
  return normalizar(linha.crianca) !== '';
}

// Cada família é identificada pelo responsável; sem nome, cada linha conta sozinha.
function chaveDaFamilia(linha: LinhaCrianca, indice: number): string {
  const responsavel = comparar(linha.responsavel);
  return responsavel ? `r:${responsavel}` : `sem:${indice}`;
}

export function contar(linhas: LinhaCrianca[]): Contagens {
  const familias = new Map<string, boolean>();
  linhas.forEach((linha, indice) => {
    const chave = chaveDaFamilia(linha, indice);
    const completa = termoAssinado(linha);
    const antes = familias.get(chave);
    familias.set(chave, antes === undefined ? completa : antes && completa);
  });

  const criancas = linhas.filter(temCrianca);
  return {
    criancas: criancas.length,
    familias: familias.size,
    completos: criancas.filter(termoAssinado).length,
    faltam: Array.from(familias.values()).filter((completa) => !completa).length,
  };
}

export function aplicarFiltro(linhas: LinhaCrianca[], filtro: Filtro): LinhaCrianca[] {
  switch (filtro) {
    case 'faltam':
      return linhas.filter((l) => !termoAssinado(l));
    case 'alergia':
      return linhas.filter(temAlergia);
    case 'remedio':
      return linhas.filter(tomaRemedio);
    case 'alimentacao':
      return linhas.filter(temRestricao);
    default:
      return linhas;
  }
}

function idadePorNascimento(nascimento?: string | null): number | null {
  const texto = normalizar(nascimento);
  const partes = texto.match(/^(\d{2})\/(\d{2})\/(\d{4})/);
  if (!partes) return null;
  const dia = Number(partes[1]);
  const mes = Number(partes[2]);
  const ano = Number(partes[3]);
  const nasceu = new Date(ano, mes - 1, dia);
  if (Number.isNaN(nasceu.getTime())) return null;
  const hoje = new Date();
  let idade = hoje.getFullYear() - nasceu.getFullYear();
  const aindaNaoFez =
    hoje.getMonth() < nasceu.getMonth() ||
    (hoje.getMonth() === nasceu.getMonth() && hoje.getDate() < nasceu.getDate());
  if (aindaNaoFez) idade -= 1;
  if (idade < 0 || idade > 120) return null;
  return idade;
}

/** "5 anos" — usa o que veio do banco e, se faltar, calcula pela data. */
export function idadeTexto(linha: LinhaCrianca): string {
  const bruto = normalizar(linha.idade);
  const numeros = bruto.replace(/\D/g, '');
  if (numeros) {
    const n = parseInt(numeros, 10);
    if (Number.isFinite(n)) return `${n} ${n === 1 ? 'ano' : 'anos'}`;
  }
  const calculada = idadePorNascimento(linha.nascimento);
  if (calculada === null) return '—';
  return `${calculada} ${calculada === 1 ? 'ano' : 'anos'}`;
}

export function digitosTelefone(valor?: string | null): string {
  return normalizar(valor).replace(/\D/g, '');
}

/** Link do WhatsApp (com o 55 quando falta). Sem telefone, não há link. */
export function linkWhatsapp(telefone: string | null | undefined, texto: string): string | null {
  const digitos = digitosTelefone(telefone);
  if (digitos.length < 10) return null;
  const comPais = digitos.startsWith('55') ? digitos : `55${digitos}`;
  return `https://wa.me/${comPais}?text=${encodeURIComponent(texto)}`;
}

export function linkDosPais(eventoId: number | string): string {
  return `${SITE}/criancas/${eventoId}`;
}

export function textoDoConvite(eventoId: number | string): string {
  return `Oi! Você vai levar seu filho ao acampamento? Preencha aqui os dados dele (remédios, alergias, alimentação) e assine a autorização. É só o seu CPF e a sua data de nascimento: ${linkDosPais(eventoId)}`;
}

/** Texto corrido para colar no WhatsApp da equipe das crianças. */
export function textoParaWhatsapp(linhas: LinhaCrianca[]): string {
  return linhas
    .filter(temCrianca)
    .map((linha) => {
      const idade = idadeTexto(linha);
      const cabeca = `*${normalizar(linha.crianca)}*${
        idade !== '—' ? ` (${idade})` : ''
      } — resp.: ${normalizar(linha.responsavel) || '—'}`;
      return [
        cabeca,
        `Alergias: ${ouTraco(linha.alergias)}`,
        `Alimentação: ${ouTraco(linha.alimentacao)}`,
        `Remédios: ${ouTraco(linha.remedios)}`,
        `Febre/dor: ${ouTraco(linha.febre_dor)}`,
        `Saúde: ${ouTraco(linha.saude)}`,
      ].join('\n');
    })
    .join('\n\n');
}

/** Famílias que marcaram "vai levar filhos" e ainda não disseram quem são. */
export function familiasSemDados(linhas: LinhaCrianca[]): { responsavel: string; telefone: string | null }[] {
  const vistos = new Set<string>();
  const lista: { responsavel: string; telefone: string | null }[] = [];
  linhas.forEach((linha, indice) => {
    if (temCrianca(linha)) return;
    const chave = chaveDaFamilia(linha, indice);
    if (vistos.has(chave)) return;
    vistos.add(chave);
    lista.push({
      responsavel: normalizar(linha.responsavel) || 'Sem nome',
      telefone: linha.telefone,
    });
  });
  return lista;
}